import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatCardModule } from "@angular/material/card";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatBadgeModule } from "@angular/material/badge";
import { EditorModule } from "@tinymce/tinymce-angular";
import {
  DocumentType,
  DOCUMENT_TYPE_LABELS,
  Document,
  getTinyMceConfig,
} from "../documents";
import { DocumentService } from "../document.service";
import { LanguageService } from "app/language/language.service";
import {
  SALES_AGREEMENT_VARIABLES,
  SalesAgreementVariable,
} from "../sales-agreement/sales-agreement-variables.constants";
import {
  CERTIFICATE_OF_CONFORMITY_VARIABLES,
  CertificateOfConformityVariable,
} from "../certificate-of-conformity/certificate-of-conformity-variables.constants";
import { environment } from "environments/environment";
import { Subject } from "rxjs";
import { takeUntil, distinctUntilChanged } from "rxjs/operators";

@Component({
  selector: "app-document-creator",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
    MatExpansionModule,
    MatIconModule,
    MatBadgeModule,
    EditorModule,
  ],
  templateUrl: "./document-creator.component.html",
  styleUrls: ["./document-creator.component.css"],
})
export class DocumentCreatorComponent implements OnInit, OnDestroy {
  private editorInstance: any;
  @ViewChild("htmlSourceEditor")
  htmlSourceEditor?: ElementRef<HTMLTextAreaElement>;

  documentTypes = Object.values(DocumentType).map((type) => ({
    value: type,
    label: DOCUMENT_TYPE_LABELS[type],
  }));

  currentDocument: Document | null = null;
  isLoading = false;

  // Sales-agreement specific features
  isHtmlMode = false;
  form: FormGroup;
  model = {
    editorData: "",
  };
  apiKey = environment.tinyMceApiKey || "";
  variables: (SalesAgreementVariable | CertificateOfConformityVariable)[] =
    SALES_AGREEMENT_VARIABLES;
  get tinyMceConfig() {
    return getTinyMceConfig(this.selectedType);
  }
  languages: any[] = [];
  confirmedLanguageName: string | null = null;
  hasUnsavedChanges = false;

  private destroy$ = new Subject<void>();
  private isProgrammaticChange = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documentService: DocumentService,
    private fb: FormBuilder,
    private languageService: LanguageService,
  ) {
    this.form = this.fb.group({
      type: [DocumentType.SALES_AGREEMENT, Validators.required],
      language: ["", Validators.required],
    });
  }

  get selectedType(): DocumentType {
    return this.form.get("type")?.value;
  }

  get selectedLanguage(): string {
    return this.form.get("language")?.value;
  }

  /**
   * Sprawdza czy zmienna jest użyta w treści dokumentu
   */
  isVariableUsed(variableKey: string): boolean {
    return this.model.editorData?.includes(variableKey) ?? false;
  }

  /**
   * Zwraca listę zmiennych użytych w dokumencie
   */
  get usedVariables(): (
    | SalesAgreementVariable
    | CertificateOfConformityVariable
  )[] {
    return this.variables.filter((v) => this.isVariableUsed(v.key));
  }

  /**
   * Zwraca listę zmiennych nieużytych w dokumencie
   */
  get unusedVariables(): (
    | SalesAgreementVariable
    | CertificateOfConformityVariable
  )[] {
    return this.variables.filter((v) => !this.isVariableUsed(v.key));
  }

  /**
   * Liczba zmiennych użytych
   */
  get usedCount(): number {
    return this.usedVariables.length;
  }

  /**
   * Liczba zmiennych nieużytych
   */
  get unusedCount(): number {
    return this.unusedVariables.length;
  }

  ngOnInit() {
    const typeParam = this.route.snapshot.queryParams["type"];
    if (
      typeParam &&
      Object.values(DocumentType).includes(typeParam as DocumentType)
    ) {
      this.form.patchValue(
        { type: typeParam as DocumentType },
        { emitEvent: false },
      );
    }

    this.loadLanguages();

    // Ustaw zmienne dla początkowego typu
    this.updateVariables();

    // Nasłuchuj zmian typu dokumentu
    this.form
      .get("type")!
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => {
        this.updateVariables();
        this.loadDocument();
      });

    // Nasłuchuj zmian języka
    this.form
      .get("language")!
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((languageName: string) => {
        if (!languageName) {
          return;
        }

        if (
          this.hasUnsavedChanges &&
          this.confirmedLanguageName &&
          this.confirmedLanguageName !== languageName
        ) {
          const shouldChangeLanguage = window.confirm(
            "Masz niezapisane zmiany. Czy na pewno chcesz zmienić język?",
          );

          if (!shouldChangeLanguage) {
            this.form.patchValue(
              { language: this.confirmedLanguageName },
              { emitEvent: false },
            );
            return;
          }
        }

        this.confirmedLanguageName = languageName;
        this.loadDocument();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLanguages(): void {
    this.languageService.getLanguages().subscribe((data: any) => {
      this.languages = [{ name: "PL" }, ...(data || [])];
    });
  }

  loadDocument() {
    const type = this.selectedType;
    const language = this.confirmedLanguageName || this.selectedLanguage;
    if (!language || !type) {
      return;
    }

    this.isLoading = true;
    this.hasUnsavedChanges = false;
    this.isProgrammaticChange = true;
    this.documentService
      .getSingleDocument({
        language: language,
        type: type,
      })
      .subscribe({
        next: (doc) => {
          this.currentDocument = doc;
          this.model.editorData = doc?.content || "";
          this.hasUnsavedChanges = false;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.currentDocument = null;
            this.model.editorData = "";
            this.hasUnsavedChanges = false;
            this.isLoading = false;
            return;
          }
          console.error(error);
          this.currentDocument = null;
          this.isLoading = false;
        },
      });
  }

  saveDocument(createNew?: boolean) {
    if (!this.currentDocument && !this.model.editorData && !createNew) return;
    if (createNew) {
      this.model.editorData = "";
    }
    const payload: Document = {
      language: this.confirmedLanguageName || this.selectedLanguage,
      content: this.model.editorData,
      type: this.selectedType,
    };

    if (this.currentDocument?._id) {
      this.documentService
        .updateDocument(this.currentDocument._id, payload)
        .subscribe(() => {
          this.hasUnsavedChanges = false;
          this.loadDocument();
        });
    } else {
      this.documentService.createDocument(payload).subscribe(() => {
        this.hasUnsavedChanges = false;
        this.loadDocument();
      });
    }
  }

  insertVariable(variable: string): void {
    if (!this.isHtmlMode && this.editorInstance) {
      this.editorInstance.insertContent(variable);
      this.model.editorData = this.editorInstance.getContent();
      this.hasUnsavedChanges = true;
      return;
    }

    this.model.editorData += variable;
    this.hasUnsavedChanges = true;
  }

  onEditorContentChange(): void {
    if (this.isProgrammaticChange) {
      this.isProgrammaticChange = false;
      return;
    }
    this.hasUnsavedChanges = true;
  }

  toggleHtmlMode(): void {
    this.isHtmlMode = !this.isHtmlMode;
  }

  onEditorReady(event: any): void {
    this.editorInstance = event?.editor;
  }

  private updateVariables(): void {
    const type = this.selectedType;
    switch (type) {
      case DocumentType.SALES_AGREEMENT:
        this.variables = SALES_AGREEMENT_VARIABLES;
        break;
      case DocumentType.CERTIFICATE_OF_CONFORMITY:
        this.variables = CERTIFICATE_OF_CONFORMITY_VARIABLES;
        break;
      default:
        this.variables = SALES_AGREEMENT_VARIABLES;
        break;
    }
  }

  resetForm(): void {
    if (this.confirmedLanguageName) {
      this.loadDocument();
    }
  }
}
